// The three hosts the frontend answers on, and the moves between them.
//
// - The apex (`vyrallab.com`, also `www.`): marketing and sign-in.
// - `my.vyrallab.com`: the app, for business users.
// - `admin.vyrallab.com`: the staff console.
//
// The API is one host for all three, and the session cookie is shared, so a
// move between hosts keeps the user signed in. The server does the real
// access checks; this file only puts each user on the right host.
//
// Local development uses the same labels under `localhost` (Chrome and
// Firefox resolve any *.localhost to 127.0.0.1, RFC 6761) or under
// REACT_APP_DEV_APEX, e.g. `my.dev.vyrallab.com`.

const PROD_APEX = 'vyrallab.com';
const DEV_APEX = 'localhost';
const DEV_LOCAL_APEX = (process.env.REACT_APP_DEV_APEX || '').toLowerCase();

function activeApex() {
  if (typeof window === 'undefined') return PROD_APEX;
  const host = (window.location.hostname || '').toLowerCase();
  if (DEV_LOCAL_APEX && (host === DEV_LOCAL_APEX || host.endsWith(`.${DEV_LOCAL_APEX}`))) return DEV_LOCAL_APEX;
  if (host === DEV_APEX || host.endsWith(`.${DEV_APEX}`)) return DEV_APEX;
  return PROD_APEX;
}

/** 'apex' | 'my' | 'admin'. A host that is neither the apex nor `admin.`
 *  counts as `my.`, so an IP address or an old tenant host shows the app. */
export function hostKind() {
  if (typeof window === 'undefined') return 'apex';
  const host = (window.location.hostname || '').toLowerCase();
  const apex = activeApex();
  if (host === apex || host === `www.${apex}`) return 'apex';
  if (host === `admin.${apex}`) return 'admin';
  return 'my';
}

/** The absolute URL of `path` on the host of `kind`. */
export function hostUrl(kind, path = '/') {
  if (typeof window === 'undefined') return path;
  const proto = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';
  const host = kind === 'apex' ? activeApex() : `${kind}.${activeApex()}`;
  return `${proto}//${host}${port}${path}`;
}

/** The host a signed-in user works on. An impersonated session is the
 *  target user, so it stays on `my.`. */
export function homeHost(user) {
  return user?.is_staff && !user?.impersonating ? 'admin' : 'my';
}

/** `/admin/users?x=1` -> `/users?x=1`. The console has no prefix on its own
 *  host; old links with the prefix still land. */
export function stripAdminPrefix(path) {
  if (path.startsWith('/admin/')) return path.slice(6);
  if (path === '/admin' || path.startsWith('/admin?') || path.startsWith('/admin#')) {
    return `/${path.slice(6)}`;
  }
  return path;
}

// Console paths from before the /admin prefix. The console now has these
// exact paths again on its own host, so old links go straight there.
const ADMIN_LEGACY_PREFIXES = [
  '/businesses', '/users', '/plans', '/ai-usage', '/integrations', '/system', '/billing',
];

const underPrefix = (path, prefix) =>
  path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`)
  || path.startsWith(`${prefix}#`);

/** Which host serves `path` (a path with its query), and the path there. */
export function hostForPath(path) {
  if (underPrefix(path, '/admin')) return ['admin', stripAdminPrefix(path)];
  if (ADMIN_LEGACY_PREFIXES.some(p => underPrefix(path, p))) return ['admin', path];
  return ['my', path];
}

/** Is `path` a console path that belongs on `admin.`? */
export const isAdminPath = (path) => hostForPath(path)[0] === 'admin';

// Wrap window.location.replace so we can detect and break redirect loops.
// If we'd bounce to the same URL >2 times in 10 seconds, abort — a clear
// sign something upstream is wrong (e.g. stale conflicting cookies).
export function safeReplace(target) {
  if (typeof window === 'undefined') return;
  try {
    const now = Date.now();
    const key = '__rv_nav_log';
    const prev = JSON.parse(sessionStorage.getItem(key) || '[]')
      .filter(e => now - e.t < 10000);
    const sameUrlCount = prev.filter(e => e.url === target).length;
    if (sameUrlCount >= 2) {
      // eslint-disable-next-line no-console
      console.error('[hosts] redirect loop suspected; aborting', { target, prev });
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, JSON.stringify([...prev.slice(-4), { url: target, t: now }]));
  } catch (_) { /* ignore */ }
  window.location.replace(target);
}
